import { useEffect, useId, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { majorToMinorUnits } from "@lms/backend/lib/courses/price-format";
import {
  COURSE_BUILDER_STEPS,
  builderStepHash,
  resolveBuilderStepFromHash,
  type CourseBuilderStep,
} from "@lms/backend/lib/courses/builder-steps";
import { useCourseLabels } from "@/components/instructor/course-labels";
import { AddLessonForm } from "@/components/instructor/add-lesson-form";
import {
  CourseOverviewForm,
  type OverviewCourse,
} from "@/components/instructor/course-overview-form";
import {
  CurriculumModuleList,
  type CurriculumModule,
} from "@/components/instructor/curriculum-module-list";
import { LessonEditorPanel } from "@/components/instructor/lesson-editor-panel";
import { CourseResourcesEditor } from "@/components/instructor/course-resources-editor";
import { buttonClasses } from "@/components/ui/button";
import { CourseAnnouncementsPanel } from "@/components/instructor/course-announcements-panel";
import { CourseCohortsPanel } from "@/components/instructor/course-cohorts-panel";
import { CourseStaffPanel } from "@/components/instructor/course-staff-panel";
import { CourseCohortDeadlinesPanel } from "@/components/instructor/course-cohort-deadlines-panel";
import { CourseCohortEventsPanel } from "@/components/instructor/course-cohort-events-panel";
import { VideoUploadIndicator } from "@/components/instructor/lesson-video-upload";
import { describePrices, usePublishCourse } from "@/components/instructor/use-publish-course";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";
import { fetchVideoUploadMode, type VideoUploadMode } from "@/services/api/video-upload";
import {
  adoptServerUploads,
  onLessonVideoReady,
  useVideoJobs,
} from "@/services/api/video-upload-store";

type BuilderModule = CurriculumModule;

type EditingLesson = {
  moduleId: string;
  lessonId: string;
  title: string;
  subtitle: string | null;
  contentType: string;
};

type Course = {
  id: string;
  title: string;
  slug: string;
  courseType: string;
  status: string;
  metadata: Record<string, unknown> | null;
  description?: string | null;
  thumbnailUrl?: string | null;
};

type CourseBuilderProps = {
  tenantSlug: string;
  courseId: string;
  initialCourse: Course;
  initialModules: BuilderModule[];
  initialPrices: { inrRupees: number; usdDollars: number };
  /** CRS-11: what this school has set up (from the builder GET). */
  capabilities?: { liveClasses: boolean };
};

const STEP_LABEL_KEYS = {
  overview: "builderStepOverview",
  curriculum: "builderStepCurriculum",
  resources: "builderStepResources",
  pricing: "builderStepPricing",
  cohorts: "builderStepCohorts",
  announcements: "builderStepAnnouncements",
  publish: "builderStepPublish",
} as const;

export function CourseBuilder({
  tenantSlug,
  courseId,
  initialCourse,
  initialModules,
  initialPrices,
  capabilities: initialCapabilities = { liveClasses: false },
}: CourseBuilderProps) {
  const t = useTranslations("instructor");
  const [course, setCourse] = useState(initialCourse);
  const [modules, setModules] = useState(initialModules);
  const [capabilities, setCapabilities] = useState(initialCapabilities);
  const [inrRupees, setInrRupees] = useState(String(initialPrices.inrRupees || ""));
  const [usdDollars, setUsdDollars] = useState(String(initialPrices.usdDollars || ""));
  const [priceSaved, setPriceSaved] = useState(false);
  const [savedPrices, setSavedPrices] = useState(initialPrices);
  const publishCourse = usePublishCourse();
  const locale = useLocale();
  const tRoot = useTranslations();
  const label = useCourseLabels();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [scanSummary, setScanSummary] = useState<string | null>(null);
  const videoJobs = useVideoJobs();
  const [uploadMode, setUploadMode] = useState<VideoUploadMode | null>(null);
  const [uploadModeError, setUploadModeError] = useState<string | null>(null);
  const uploadRunning = Object.values(videoJobs).some((job) => job.phase === "uploading");
  const [notesScanSummary, setNotesScanSummary] = useState<string | null>(null);
  const [moduleTitle, setModuleTitle] = useState("");
  const [editingLesson, setEditingLesson] = useState<EditingLesson | null>(null);
  const [activeModuleId, setActiveModuleId] = useState<string | null>(
    initialModules[0]?.id ?? null,
  );
  const [sessionQuery, setSessionQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<CourseBuilderStep>("curriculum");
  const lessonEditorRef = useRef<HTMLDivElement>(null);
  const convertsNoteId = useId();

  useEffect(() => {
    const syncFromHash = () => setStep(resolveBuilderStepFromHash(window.location.hash));
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, []);

  // Opening the editor is an event, not a synchronisation: `openLessonEditor`
  // switches step directly rather than reacting to `editingLesson` in an effect.
  // Only the scroll — a DOM side effect that needs the editor rendered first —
  // stays here.
  useEffect(() => {
    if (!editingLesson) return;
    lessonEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editingLesson]);

  function openLessonEditor(lesson: EditingLesson) {
    setEditingLesson(lesson);
    setStep("curriculum");
  }

  function goToStep(next: CourseBuilderStep) {
    setStep(next);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", builderStepHash(next));
    }
  }

  const lessonCount = modules.reduce((n, mod) => n + mod.lessons.length, 0);
  const savedPriceText = describePrices(
    [
      { currency: "INR", amount: majorToMinorUnits(savedPrices.inrRupees, "INR"), isActive: true },
      { currency: "USD", amount: majorToMinorUnits(savedPrices.usdDollars, "USD"), isActive: true },
    ],
    locale,
  );

  const stepIndex = COURSE_BUILDER_STEPS.indexOf(step);
  const prevStep = stepIndex > 0 ? COURSE_BUILDER_STEPS[stepIndex - 1] : null;
  const nextStep =
    stepIndex >= 0 && stepIndex < COURSE_BUILDER_STEPS.length - 1
      ? COURSE_BUILDER_STEPS[stepIndex + 1]
      : null;

  // Uploading converts a text session to video (upload-lesson-video.ts), which changes how
  // students complete it. Said once above the list, not on each of up to 90 rows.
  const hasTextSessions = modules.some((mod) =>
    mod.lessons.some((lesson) => lesson.contentType === "text"),
  );

  async function reloadBuilder() {
    const res = await apiFetch(`/v1/instructor/courses/${courseId}`);
    if (!res.ok) {
      setError(t("reloadFailed"));
      return;
    }
    const json = await res.json();
    setCourse(json.data.course as Course);
    const nextModules = json.data.modules as BuilderModule[];
    setModules(nextModules);
    if (json.data.capabilities) setCapabilities(json.data.capabilities);
    if (
      nextModules.length > 0 &&
      (activeModuleId == null || !nextModules.some((mod) => mod.id === activeModuleId))
    ) {
      setActiveModuleId(nextModules[0]!.id);
    }
  }

  async function handleAddModule(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const res = await apiFetch(`/v1/instructor/courses/${courseId}/modules`, {
      method: "POST",
      body: JSON.stringify({ title: moduleTitle.trim() }),
    });
    if (!res.ok) {
      const json = await res.json();
      setError(errorText(json, "addModuleFailed"));
      return;
    }
    setModuleTitle("");
    // CRS-4: the new module is where the next lesson goes.
    const created = (await res.json().catch(() => null)) as {
      data?: { module?: { id?: string } };
    } | null;
    if (created?.data?.module?.id) setActiveModuleId(created.data.module.id);
    await reloadBuilder();
  }

  // CRS-9: with Bunny on, the server video-folder scan is off (and refused by the API).
  useEffect(() => {
    let cancelled = false;
    fetchVideoUploadMode()
      .then((mode) => {
        if (!cancelled) setUploadMode(mode);
      })
      .catch((err) => {
        // F16: say why uploads are unavailable (the API's message), never a silent control.
        if (!cancelled) setUploadModeError(errorText(err, "uploadVideoFailed"));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // CRS-2: uploads Bunny is still processing come back with the builder, so polling resumes
  // after a reload, a closed tab or an expired session — not only in the tab that uploaded.
  useEffect(() => {
    adoptServerUploads(
      courseId,
      modules.flatMap((mod) => mod.lessons),
    );
  }, [courseId, modules]);

  // A lesson's new video went live (or a local upload finished): show it.
  const reloadRef = useRef(reloadBuilder);
  reloadRef.current = reloadBuilder;
  useEffect(
    () =>
      onLessonVideoReady((_lessonId, readyCourseId) => {
        if (readyCourseId === courseId) void reloadRef.current();
      }),
    [courseId],
  );

  async function handleScanVideos() {
    setError(null);
    setScanSummary(null);
    const res = await apiFetch(`/v1/instructor/courses/${courseId}/videos/scan`, {
      method: "POST",
    });
    if (!res.ok) {
      const json = await res.json();
      setError(errorText(json, "scanFailed"));
      return;
    }
    const json = await res.json();
    const results = json.data.results as { linked: boolean }[];
    const linkedCount = results.filter((r) => r.linked).length;
    setScanSummary(t("scanResult", { linked: linkedCount, total: results.length }));
    await reloadBuilder();
  }

  async function handleScanStudyNotes() {
    setError(null);
    setNotesScanSummary(null);
    const res = await apiFetch(`/v1/instructor/courses/${courseId}/study-notes/scan`, {
      method: "POST",
    });
    if (!res.ok) {
      const json = await res.json();
      setError(errorText(json, "scanNotesFailed"));
      return;
    }
    const json = await res.json();
    const results = json.data.results as { linked: boolean }[];
    const linkedCount = results.filter((r) => r.linked).length;
    setNotesScanSummary(t("scanNotesResult", { linked: linkedCount, total: results.length }));
    await reloadBuilder();
  }

  async function handleSavePrices(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPriceSaved(false);
    const next = {
      inrRupees: inrRupees === "" ? 0 : Number(inrRupees),
      usdDollars: usdDollars === "" ? 0 : Number(usdDollars),
    };
    // CRS-5: dropping a live course's price to 0 makes it free for anyone — say so first.
    const becomesFree =
      (savedPrices.inrRupees > 0 && next.inrRupees === 0) ||
      (savedPrices.usdDollars > 0 && next.usdDollars === 0);
    const confirmFree = course.status === "published" && becomesFree;
    if (confirmFree && !window.confirm(t("priceZeroOnPublishedWarning"))) {
      return;
    }
    // R3-P2: the server refuses to make a published paid course free without confirmFree.
    const put = (body: typeof next & { confirmFree?: true }) =>
      apiFetch(`/v1/instructor/courses/${courseId}/prices`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    let res = await put(confirmFree ? { ...next, confirmFree: true } : next);
    if (!res.ok) {
      const err = await readApiErrorResponse(res);
      // This screen's idea of the saved prices was stale (e.g. priced in another tab): ask now.
      if (err.code !== "CONFIRM_FREE_REQUIRED") {
        setError(errorText(err, "savePricesFailed"));
        return;
      }
      if (!window.confirm(t("priceZeroOnPublishedWarning"))) return;
      res = await put({ ...next, confirmFree: true });
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "savePricesFailed"));
        return;
      }
    }
    setSavedPrices(next);
    setPriceSaved(true);
  }

  async function handlePublish() {
    setError(null);
    if (course.status !== "published") {
      // CRS-5: confirmation naming the price, or that the course is free.
      const outcome = await publishCourse(courseId);
      if (outcome.status === "cancelled") return;
      if (outcome.status === "failed") {
        setError(errorText(outcome.error, "publishFailed"));
        return;
      }
      setCourse((current) => ({ ...current, ...(outcome.course as Partial<Course>) }));
      return;
    }
    const res = await apiFetch(`/v1/instructor/courses/${courseId}/unpublish`, {
      method: "POST",
    });
    if (!res.ok) {
      setError(errorText(await readApiErrorResponse(res), "publishFailed"));
      return;
    }
    const json = await res.json();
    setCourse(json.data.course as Course);
  }

  return (
    <div className="space-y-6" data-testid="course-builder" data-builder-step={step}>
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--border)] pb-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
            {t("builderWizardLabel")}
          </p>
          <h1 className="mt-1 text-2xl font-semibold">{course.title}</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {label.type(course.courseType)} · {label.status(course.status)} · /{course.slug}
          </p>
          {course.courseType === "retreat" && course.metadata?.location ? (
            <p className="mt-1 text-sm text-[var(--text-muted)]" data-testid="retreat-location">
              {t("retreatLocation", { location: String(course.metadata.location) })}
            </p>
          ) : null}
        </div>
        <VideoUploadIndicator />
        {uploadModeError ? (
          <p
            role="alert"
            className="text-sm text-[var(--danger)]"
            data-testid="video-upload-mode-error"
          >
            {uploadModeError}
          </p>
        ) : null}
        <button
          type="button"
          data-testid="course-publish-toggle"
          onClick={handlePublish}
          className="rounded border px-4 py-2 text-sm hover:bg-[var(--surface-3)]"
        >
          {course.status === "published" ? t("unpublish") : t("publish")}
        </button>
      </header>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside
          className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-3"
          data-testid="builder-step-nav"
        >
          <p className="px-2 text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
            {t("builderStepsTitle")}
          </p>
          <ol className="mt-3 space-y-1">
            {COURSE_BUILDER_STEPS.map((item, index) => {
              const active = item === step;
              return (
                <li key={item}>
                  <button
                    type="button"
                    data-testid={`builder-step-${item}`}
                    onClick={() => goToStep(item)}
                    className={
                      active
                        ? "flex w-full items-center gap-2 rounded-md bg-[var(--surface-1)] px-2 py-2 text-left text-sm font-medium text-[var(--text-primary)] shadow-sm ring-1 ring-[var(--border)]"
                        : "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-[var(--text-muted)] hover:bg-[var(--surface-3)]"
                    }
                  >
                    <span
                      className={
                        active
                          ? "flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--ink-12)] text-xs text-[var(--ink-1)]"
                          : "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[var(--border-strong)] text-xs"
                      }
                    >
                      {index + 1}
                    </span>
                    <span>{t(STEP_LABEL_KEYS[item])}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <div className="min-w-0 space-y-6">
          {step === "overview" ? (
            <section data-testid="builder-panel-overview" className="space-y-3">
              <h2 className="text-lg font-medium">{t("builderStepOverview")}</h2>
              <p className="text-sm text-[var(--text-muted)]">{t("builderOverviewHint")}</p>
              <p className="text-sm text-[var(--text-muted)]">
                {label.type(course.courseType)} · {label.status(course.status)}
              </p>
              <CourseOverviewForm
                courseId={courseId}
                course={course as OverviewCourse}
                onSaved={async (saved) => {
                  setCourse((current) => ({ ...current, ...saved }));
                  await reloadBuilder();
                }}
              />
              <CourseStaffPanel courseId={courseId} />
            </section>
          ) : null}

          {step === "curriculum" ? (
            <div className="space-y-8" data-testid="builder-panel-curriculum">
              <section>
                <h2 className="text-lg font-medium">{t("modulesTitle")}</h2>
                <p className="mt-1 text-sm text-[var(--text-muted)]">{t("sessionSearchHint")}</p>
                <input
                  data-testid="session-search"
                  className="mt-3 w-full max-w-md rounded border px-3 py-2 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                  placeholder={t("sessionSearchPlaceholder")}
                  value={sessionQuery}
                  onChange={(e) => setSessionQuery(e.target.value)}
                />
                {hasTextSessions ? (
                  <p
                    id={convertsNoteId}
                    className="mt-3 text-sm text-[var(--text-muted)]"
                    data-testid="upload-video-converts-note"
                  >
                    {t("uploadVideoConvertsNote")}
                  </p>
                ) : null}
                <CurriculumModuleList
                  courseId={courseId}
                  modules={modules}
                  filter={sessionQuery}
                  uploadRunning={uploadRunning}
                  uploadNotConfigured={uploadMode === "not_configured"}
                  convertsNoteId={convertsNoteId}
                  onSelectModule={setActiveModuleId}
                  onEditLesson={(moduleId, lesson) =>
                    openLessonEditor({
                      moduleId,
                      lessonId: lesson.id,
                      title: lesson.title,
                      subtitle: lesson.subtitle ?? null,
                      contentType: lesson.contentType,
                    })
                  }
                  onModulesChange={setModules}
                  onChanged={() => reloadBuilder()}
                  onDeleted={({ moduleId, lessonId }) => {
                    if (
                      editingLesson &&
                      (editingLesson.lessonId === lessonId ||
                        (!lessonId && editingLesson.moduleId === moduleId))
                    ) {
                      setEditingLesson(null);
                    }
                  }}
                />

                {editingLesson ? (
                  <div
                    ref={lessonEditorRef}
                    className="mt-6 scroll-mt-24"
                    data-testid="lesson-editor-anchor"
                  >
                    <LessonEditorPanel
                      courseId={courseId}
                      moduleId={editingLesson.moduleId}
                      lessonId={editingLesson.lessonId}
                      lessonTitle={editingLesson.title}
                      lessonSubtitle={editingLesson.subtitle}
                      contentType={editingLesson.contentType}
                      liveClasses={capabilities.liveClasses}
                      onClose={() => setEditingLesson(null)}
                      onSaved={reloadBuilder}
                    />
                  </div>
                ) : null}

                <form onSubmit={handleAddModule} className="mt-4 flex gap-2">
                  <input
                    data-testid="add-module-title"
                    className="flex-1 rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                    placeholder={t("modulePlaceholder")}
                    value={moduleTitle}
                    onChange={(e) => setModuleTitle(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    data-testid="add-module-submit"
                    className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)]"
                  >
                    {t("addModule")}
                  </button>
                </form>
              </section>

              <AddLessonForm
                courseId={courseId}
                modules={modules}
                activeModuleId={activeModuleId}
                onActiveModuleChange={setActiveModuleId}
                liveClasses={capabilities.liveClasses}
                onCreated={reloadBuilder}
              />

              {/* The folder scans need shell access to the server, so they are
                  for importing an existing library in bulk — not the everyday
                  way to add a session. Per-lesson upload above is that. */}
              <details
                data-testid="curriculum-bulk-import"
                className="rounded-lg border border-[var(--border)] p-4"
              >
                <summary className="cursor-pointer font-medium">{t("bulkImportTitle")}</summary>
                <p className="mt-2 text-sm text-[var(--text-muted)]">{t("bulkImportHint")}</p>
                <div className="mt-4 space-y-6">
                  {uploadMode === "bunny" ? (
                    <p
                      className="text-sm text-[var(--text-muted)]"
                      data-testid="video-scan-bunny-note"
                    >
                      {t("videoScanBunnyNote")}
                    </p>
                  ) : (
                    <section data-testid="course-video-folder">
                      <h3 className="font-medium">{t("videosTitle")}</h3>
                      <p className="mt-1 text-sm text-[var(--text-muted)]">
                        {t("videosHint", { tenantSlug, courseSlug: initialCourse.slug })}
                      </p>
                      <button
                        type="button"
                        data-testid="scan-video-folder"
                        onClick={handleScanVideos}
                        className={`mt-3 ${buttonClasses({ variant: "secondary", size: "sm" })}`}
                      >
                        {t("scanVideos")}
                      </button>
                      {scanSummary ? (
                        <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                          {scanSummary}
                        </p>
                      ) : null}
                    </section>
                  )}

                  <section data-testid="course-study-notes-folder">
                    <h3 className="font-medium">{t("studyNotesTitle")}</h3>
                    <p className="mt-1 text-sm text-[var(--text-muted)]">
                      {t("studyNotesHint", { tenantSlug, courseSlug: initialCourse.slug })}
                    </p>
                    <button
                      type="button"
                      data-testid="scan-study-notes-folder"
                      onClick={handleScanStudyNotes}
                      className={`mt-3 ${buttonClasses({ variant: "secondary", size: "sm" })}`}
                    >
                      {t("scanStudyNotes")}
                    </button>
                    {notesScanSummary ? (
                      <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                        {notesScanSummary}
                      </p>
                    ) : null}
                  </section>
                </div>
              </details>
            </div>
          ) : null}

          {step === "resources" ? <CourseResourcesEditor courseId={courseId} /> : null}

          {step === "pricing" ? (
            <section data-testid="course-pricing-editor">
              <h2 className="text-lg font-medium">{t("pricingTitle")}</h2>
              <p className="mt-1 text-sm text-[var(--text-muted)]">{t("pricingHint")}</p>
              <form onSubmit={handleSavePrices} className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="text-[var(--text-muted)]">{t("inrLabel")}</span>
                  <input
                    data-testid="price-inr"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    className="mt-1 w-full rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                    value={inrRupees}
                    onChange={(e) => setInrRupees(e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-[var(--text-muted)]">{t("usdLabel")}</span>
                  <input
                    data-testid="price-usd"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    className="mt-1 w-full rounded border px-3 py-2 border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                    value={usdDollars}
                    onChange={(e) => setUsdDollars(e.target.value)}
                  />
                </label>
                <p className="text-xs text-[var(--text-muted)] sm:col-span-2">
                  {t("priceDecimalsHint")}
                </p>
                <div className="flex items-center gap-3 sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="price-save"
                    className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)]"
                  >
                    {t("savePrices")}
                  </button>
                  {priceSaved ? (
                    <span className="text-sm text-green-700 dark:text-green-400">
                      {t("pricesSaved")}
                    </span>
                  ) : null}
                </div>
              </form>
            </section>
          ) : null}

          {step === "cohorts" ? (
            <div className="space-y-8" data-testid="builder-panel-cohorts">
              <section id="cohorts" className="scroll-mt-24">
                <CourseCohortsPanel courseId={courseId} />
              </section>
              <section id="cohort-live" className="scroll-mt-24">
                <CourseCohortEventsPanel courseId={courseId} />
                <CourseCohortDeadlinesPanel courseId={courseId} />
              </section>
            </div>
          ) : null}

          {step === "announcements" ? (
            <section
              id="announcements"
              className="scroll-mt-24"
              data-testid="builder-panel-announcements"
            >
              <CourseAnnouncementsPanel courseId={courseId} />
            </section>
          ) : null}

          {step === "publish" ? (
            <section data-testid="builder-panel-publish" className="space-y-4">
              <h2 className="text-lg font-medium">{t("builderStepPublish")}</h2>
              <p className="text-sm text-[var(--text-muted)]">{t("builderPublishHint")}</p>
              <div className="rounded-lg border border-[var(--border)] p-4">
                <p className="text-sm">
                  {t("builderStatusLabel")}: <strong>{label.status(course.status)}</strong>
                </p>
                <p className="mt-2 text-sm text-[var(--text-muted)]">
                  {t("builderPublishModules", { count: modules.length })} ·{" "}
                  {t("builderPublishLessons", {
                    count: modules.reduce((n, mod) => n + mod.lessons.length, 0),
                  })}
                </p>
                {/* CRS-5: what the server will check, said before the button is pressed. */}
                <ul className="mt-2 space-y-1 text-sm" data-testid="publish-checklist">
                  <li data-testid="publish-check-lessons">
                    {lessonCount > 0
                      ? t("builderPublishHasLessons")
                      : t("builderPublishNeedsLesson")}
                  </li>
                  <li data-testid="publish-check-price">
                    {savedPriceText
                      ? t("builderPublishPrice", { prices: savedPriceText })
                      : t("builderPublishFree")}
                  </li>
                </ul>
                <button
                  type="button"
                  data-testid="course-publish-toggle-step"
                  onClick={handlePublish}
                  className="mt-4 rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)]"
                >
                  {course.status === "published" ? t("unpublish") : t("publish")}
                </button>
              </div>
            </section>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] pt-4">
            <button
              type="button"
              data-testid="builder-step-prev"
              disabled={!prevStep}
              onClick={() => prevStep && goToStep(prevStep)}
              className="rounded border px-4 py-2 text-sm disabled:opacity-40 hover:bg-[var(--surface-3)]"
            >
              {t("builderPrev")}
            </button>
            <p className="text-xs text-[var(--text-muted)]">
              {t("builderStepProgress", {
                current: stepIndex + 1,
                total: COURSE_BUILDER_STEPS.length,
              })}
            </p>
            <button
              type="button"
              data-testid="builder-step-next"
              disabled={!nextStep}
              onClick={() => nextStep && goToStep(nextStep)}
              className="rounded bg-[var(--ink-12)] px-4 py-2 text-sm text-[var(--ink-1)] disabled:opacity-40"
            >
              {t("builderNext")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
